/** @file World-space unit vectors of an orthographic camera from its yaw and pitch in degrees. */
import type { Vec3 } from '../vec3/vec3';

const RAD = Math.PI / 180;

/** Screen right, screen up and toward-the-viewer directions in world space (orthonormal). */
export type OrthoBasis = { right: Vec3; up: Vec3; back: Vec3 };

/**
 * Camera yaw 0 stands south of the subject looking north (+y), pitched down by `cameraPitchDeg`. Camera yaw
 * orbits opposite to node yaw so that a node's azimuth (relative to the camera) is node yaw + camera yaw:
 * the camera turns counter-clockwise seen from above (yaw 90 stands east looking west). View forward g cos(e) - z sin(e) with g the ground heading; back is its
 * negation and up = g sin(e) + z cos(e).
 */
export function orthoBasis(cameraYawDeg: number, cameraPitchDeg: number): OrthoBasis {
  const c = cameraYawDeg * RAD;
  const e = cameraPitchDeg * RAD;
  const cc = Math.cos(c);
  const sc = Math.sin(c);
  const ce = Math.cos(e);
  const se = Math.sin(e);
  return {
    right: [cc, sc, 0],
    up: [-sc * se, cc * se, ce],
    back: [sc * ce, -cc * ce, se],
  };
}
